"use client";

import { c as _c } from "react/compiler-runtime";
import { useContext, useEffect, useState } from "react";
import type { ApiWave } from "@/generated/models/ApiWave";
import { AuthContext } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { useMutation } from "@tanstack/react-query";
import { commonApiDeleteWithBody, commonApiPost } from "@/services/api/common-api";
import type { ApiWaveSubscriptionActions } from "@/generated/models/ApiWaveSubscriptionActions";
import { WAVE_DEFAULT_SUBSCRIPTION_ACTIONS } from "@/components/react-query-wrapper/utils/query-utils";
import SecondaryButton from "@/components/utils/button/SecondaryButton";
import ActionButton from "@/components/utils/button/ActionButton";
enum WaveItemFollowState {
  FOLLOWING = "FOLLOWING",
  FOLLOW = "FOLLOW",
  CANT_FOLLOW = "CANT_FOLLOW",
}
export default function WaveItemFollow(t0) {
  const $ = _c(73);
  const {
    wave
  } = t0;
  const {
    connectedProfile,
    activeProfileProxy,
    setToast,
    requestAuth
  } = useContext(AuthContext);
  const {
    onWaveFollowChange
  } = useContext(ReactQueryWrapperContext);
  const isSubscribed = !!wave.subscribed_actions.length;
  const label = isSubscribed ? "Following" : "Follow";
  let t1;
  if ($[0] !== activeProfileProxy || $[1] !== connectedProfile?.handle) {
    t1 = () => !!connectedProfile?.handle && !activeProfileProxy;
    $[0] = activeProfileProxy;
    $[1] = connectedProfile?.handle;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const getCanSubscribe = t1;
  const [canSubscribe, setCanSubscribe] = useState(getCanSubscribe());
  let t2;
  if ($[3] !== getCanSubscribe) {
    t2 = () => setCanSubscribe(getCanSubscribe());
    $[3] = getCanSubscribe;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== activeProfileProxy || $[6] !== connectedProfile) {
    t3 = [connectedProfile, activeProfileProxy];
    $[5] = activeProfileProxy;
    $[6] = connectedProfile;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  useEffect(t2, t3);
  const [mutating, setMutating] = useState(false);
  let t4;
  if ($[8] !== canSubscribe || $[9] !== mutating) {
    t4 = () => mutating || !canSubscribe;
    $[8] = canSubscribe;
    $[9] = mutating;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const getIsDisabled = t4;
  let t5;
  if ($[11] !== getIsDisabled) {
    t5 = getIsDisabled();
    $[11] = getIsDisabled;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const [isDisabled, setIsDisabled] = useState(t5);
  let t6;
  if ($[13] !== getIsDisabled) {
    t6 = () => setIsDisabled(getIsDisabled());
    $[13] = getIsDisabled;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== canSubscribe || $[16] !== mutating) {
    t7 = [mutating, canSubscribe];
    $[15] = canSubscribe;
    $[16] = mutating;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  useEffect(t6, t7);
  let t8;
  if ($[18] !== canSubscribe || $[19] !== isSubscribed) {
    t8 = () => {
      if (!canSubscribe) {
        return WaveItemFollowState.CANT_FOLLOW;
      }
      if (isSubscribed) {
        return WaveItemFollowState.FOLLOWING;
      }
      return WaveItemFollowState.FOLLOW;
    };
    $[18] = canSubscribe;
    $[19] = isSubscribed;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const getState = t8;
  let t9;
  if ($[21] !== getState) {
    t9 = getState();
    $[21] = getState;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const [state, setState] = useState(t9);
  let t10;
  if ($[23] !== getState) {
    t10 = () => setState(getState());
    $[23] = getState;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== canSubscribe || $[26] !== isSubscribed) {
    t11 = [isSubscribed, canSubscribe];
    $[25] = canSubscribe;
    $[26] = isSubscribed;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  useEffect(t10, t11);
  let t12;
  if ($[28] !== wave.id) {
    t12 = async () => {
      await commonApiPost({
        endpoint: `waves/${wave.id}/subscriptions`,
        body: {
          actions: WAVE_DEFAULT_SUBSCRIPTION_ACTIONS
        }
      });
    };
    $[28] = wave.id;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== onWaveFollowChange || $[31] !== wave.id) {
    t13 = () => {
      onWaveFollowChange({
        waveId: wave.id,
        following: true
      });
    };
    $[30] = onWaveFollowChange;
    $[31] = wave.id;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== setToast) {
    t14 = error => {
      setToast({
        message: error as unknown as string,
        type: "error"
      });
    };
    $[33] = setToast;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      setMutating(false);
    };
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t12 || $[37] !== t13 || $[38] !== t14) {
    t16 = {
      mutationFn: t12,
      onSuccess: t13,
      onError: t14,
      onSettled: t15
    };
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  const subscribeMutation = useMutation(t16);
  let t17;
  if ($[40] !== wave.id) {
    t17 = async () => {
      await commonApiDeleteWithBody({
        endpoint: `waves/${wave.id}/subscriptions`,
        body: {
          actions: WAVE_DEFAULT_SUBSCRIPTION_ACTIONS
        }
      });
    };
    $[40] = wave.id;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== onWaveFollowChange || $[43] !== wave.id) {
    t18 = () => {
      onWaveFollowChange({
        waveId: wave.id,
        following: false
      });
    };
    $[42] = onWaveFollowChange;
    $[43] = wave.id;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== setToast) {
    t19 = error_0 => {
      setToast({
        message: error_0 as unknown as string,
        type: "error"
      });
    };
    $[45] = setToast;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      setMutating(false);
    };
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t17 || $[49] !== t18 || $[50] !== t19) {
    t21 = {
      mutationFn: t17,
      onSuccess: t18,
      onError: t19,
      onSettled: t20
    };
    $[48] = t17;
    $[49] = t18;
    $[50] = t19;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  const unSubscribeMutation = useMutation(t21);
  let t22;
  if ($[52] !== isSubscribed || $[53] !== requestAuth || $[54] !== subscribeMutation || $[55] !== unSubscribeMutation) {
    t22 = async () => {
      setMutating(true);
      const {
        success
      } = await requestAuth();
      if (!success) {
        setMutating(false);
        return;
      }
      if (isSubscribed) {
        await unSubscribeMutation.mutateAsync();
        return;
      }
      await subscribeMutation.mutateAsync();
    };
    $[52] = isSubscribed;
    $[53] = requestAuth;
    $[54] = subscribeMutation;
    $[55] = unSubscribeMutation;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  const onSubscribe = t22;
  if (state === WaveItemFollowState.FOLLOWING) {
    let t23;
    if ($[57] !== mutating) {
      t23 = !mutating && <svg className="tw-h-3 tw-w-3" viewBox="0 0 17 15" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="17" height="15"><path d="M14.7953 0.853403L5.24867 10.0667L2.71534 7.36007C2.24867 6.92007 1.51534 6.8934 0.982005 7.26674C0.462005 7.6534 0.315338 8.3334 0.635338 8.88007L3.63534 13.7601C3.92867 14.2134 4.43534 14.4934 5.00867 14.4934C5.55534 14.4934 6.07534 14.2134 6.36867 13.7601C6.84867 13.1334 16.0087 2.2134 16.0087 2.2134C17.2087 0.986737 15.7553 -0.093263 14.7953 0.84007V0.853403Z" fillRule="evenodd" clipRule="evenodd" fill="currentColor" /></svg>;
      $[57] = mutating;
      $[58] = t23;
    } else {
      t23 = $[58];
    }
    const t24 = !mutating && label;
    let t25;
    if ($[59] !== isDisabled || $[60] !== mutating || $[61] !== onSubscribe || $[62] !== t23 || $[63] !== t24) {
      t25 = <SecondaryButton onClicked={onSubscribe} disabled={isDisabled} loading={mutating} size="sm">{t23}{t24}</SecondaryButton>;
      $[59] = isDisabled;
      $[60] = mutating;
      $[61] = onSubscribe;
      $[62] = t23;
      $[63] = t24;
      $[64] = t25;
    } else {
      t25 = $[64];
    }
    return t25;
  }
  let t23;
  if ($[65] !== mutating) {
    t23 = !mutating && <svg className="tw-h-5 tw-w-5 -tw-ml-1" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>;
    $[65] = mutating;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  const t24 = !mutating && label;
  let t25;
  if ($[67] !== isDisabled || $[68] !== mutating || $[69] !== onSubscribe || $[70] !== t23 || $[71] !== t24) {
    t25 = <ActionButton onClicked={onSubscribe} disabled={isDisabled} loading={mutating} size="sm">{t23}{t24}</ActionButton>;
    $[67] = isDisabled;
    $[68] = mutating;
    $[69] = onSubscribe;
    $[70] = t23;
    $[71] = t24;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  return t25;
}